import _ from 'lodash';
import {action, makeObservable, observable} from 'mobx';

import {
    IAssetBundle,
    IAssetBundleManifest,
    ISceneBundle,
    IStoryBundle,
    JsonStoryBundle
} from 'models/storyRelease/IAssetBundleManifest';
import {IAssetBundleInfo, IYamlManifest} from 'models/storyRelease/Manifest';
import {ReleaseInfo} from "models/storyRelease/ReleaseInfo";

export enum ProgressState {
    ERROR = -1,
    NOT_STARTED = 0,
    IN_PROGRESS = 1,
    COMPLETED = 2,
    PROGRESS_LOST = 3,
}

export class AssetBundle {
    @observable public release_id: number;
    @observable public name: string;
    @observable public index: number; // Calculated: see load method
    @observable public is_scene: boolean; // Calculated: see load method
    @observable public url: string;
    @observable public dependencies: string[];

    @observable public progress_state: ProgressState = ProgressState.NOT_STARTED;

    /**
     * Used to store and get data from the window
     * Mimic the behaviour in React.jslib in rascality-kids-unity-app
     */
    public get DataPath(): string {
        return `${this.url.split('/').at(-1)}_asset_data`
    }

    /**
     * Used to store and get data from the window
     * Mimic the behaviour in React.jslib in rascality-kids-unity-app
     */
    public get PointerPath(): string {
        return `${this.url.split('/').at(-1)}_asset_pointer`
    }

    constructor() {
    }

    private withData(data: IAssetBundle): AssetBundle {
        this.load(data);
        return this;
    }

    @action.bound
    private load(data: IAssetBundle): void {
        this.name = data.name;
        let index: number = parseInt(data.name.replace(/\D/g, ''));
        this.index = isNaN(index) ? 0 : index;
        this.is_scene = !(data.name.includes('assets') || data.name.includes('settings'));
        this.url = data.url;
        this.dependencies = data.dependencies;
    }

    public withYamlData(yaml: IAssetBundleInfo): AssetBundle {
        this.loadFromYaml(yaml);
        return this;
    }

    @action.bound
    public loadFromYaml(yaml: IAssetBundleInfo): void {
        console.log(yaml);
        this.name = yaml.Name;
        this.dependencies = _.values(yaml.Dependencies);
    }

    public isDownloadComplete(): boolean {
        return this.progress_state == ProgressState.COMPLETED; //TODO: && fileExists()
    }

    public static Load(bundle: IAssetBundle | null): AssetBundle {
        return !bundle ? null : new AssetBundle().withData(bundle);
    }

    public static LoadAllFromArray(array: IAssetBundle[]): AssetBundle[] {
        return !array ? [] : array.map(bundle => AssetBundle.Load(bundle));
    }

    public static LoadAllFromYamlObject(obj: IYamlManifest): AssetBundle[] {
        let bundleInfos: Record<string, IAssetBundleInfo> = obj.AssetBundleManifest.AssetBundleInfos;
        return _.map(_.values(bundleInfos), (value: IAssetBundleInfo) => new AssetBundle().withYamlData(value));
    }
}

export class AssetBundleManifest {
    @observable public asset_bundles: AssetBundle[];
    @observable public settings_bundle: AssetBundle | null = null;

    constructor() {
        makeObservable(this);

        this.withData = this.withData.bind(this);
    }

    public withData(data: IAssetBundleManifest): AssetBundleManifest {
        this.load(data);
        return this;
    }

    @action.bound
    public load(data: IAssetBundleManifest): void {
        if (data) {
            this.asset_bundles = AssetBundle.LoadAllFromArray(data.asset_bundles);
            this.settings_bundle = AssetBundle.Load(data.settings_bundle);
        }
    }

    public withYamlData(yaml: IYamlManifest, releaseInfo: ReleaseInfo): AssetBundleManifest {
        this.loadFromYaml(yaml);
        this.loadSettingsBundleFromAssetBundles(releaseInfo);
        return this;
    }

    /*
        * The settings bundle is not part of the yaml manifest, but is specified in the release info
        * So we need to load it from the asset bundles array and remove it from there.
        * We don't need to do this if we load from the psql data, because there it is already
        * specified separately.
     */
    private loadSettingsBundleFromAssetBundles(releaseInfo: ReleaseInfo) {
        if (!releaseInfo || !releaseInfo.settings_bundle_name) return null;

        this.settings_bundle = this.getBundleByName(releaseInfo.settings_bundle_name);
        if (this.settings_bundle) {
            // Remove settings bundle from asset_bundles array
            this.asset_bundles = this.asset_bundles.filter(bundle => bundle.name !== this.settings_bundle.name);
        }
    }

    @action.bound
    public loadFromYaml(yaml: IYamlManifest): void {
        this.asset_bundles = AssetBundle.LoadAllFromYamlObject(yaml)
        this.settings_bundle = null // Not in yaml
    }

    public setReleaseId(id: number): void {
        _.map(this.asset_bundles, bundle => bundle.release_id = id);
    }

    public getBundleByName(name: string): AssetBundle | undefined {
        return _.find(this.asset_bundles, bundle => bundle.name === name);
    }

    /**
     * Convert data from the form that it is stored in psql (story_releases.unity_asset_manifest)
     *  {
     *      asset_bundles: [{
     *          url: string,
     *          name: string,
     *          dependencies: [string] // name
     *      }],
     *      settings_bundle: {
     *          url: string,
     *          name: string,
     *          dependencies: [string] // name
     *      }
     *  }
     * to IStoryBundle:
     *  {
     *      scene_bundles: [{
     *          name: string;
     *          asset_bundles: [{
     *              release_id: number;
     *              name: string;
     *              index: number;
     *              is_scene: boolean;
     *              url: string;
     *              dependencies: [string]; // name
     *          }];
     *      }];
     *      settings_bundle: {
     *          release_id: number;
     *          name: string;
     *          index: number;
     *          is_scene: boolean;
     *          url: string;
     *          dependencies: [string]; // name
     *      } | null;
     *      current_scene_bundle: string;
     *  }
     * Each bundle without 'assets' or 'settings' in its name is assumed to be a scene bundle (see also AssetBundle.is_scene)
     * Each scene bundle is assumed to have a number in its name
     * Each scene bundle is sorted by this number (see also AssetBundle.index)
     * In IStoryBundle.scene_bundles.asset_bundles is an array containing:
     *      - the last element of the array is the scene's asset bundle info
     *      - each element, except the last, is info about asset bundles that the scene's asset bundle depends on
     * @returns IStoryBundle
     */
    public getStoryBundle(): IStoryBundle {
        // See AssetBundle.load to understand our assumptions
        let orderedScenes: AssetBundle[] = _.orderBy(_.filter(this.asset_bundles, bundle => bundle.is_scene), bundle => bundle.index);

        let orderedBundles: IStoryBundle = {
            scene_bundles: [],
            settings_bundle: this.settings_bundle,
            current_scene_bundle: ''
        };

        _.map(orderedScenes, scene => {

            let sceneBundle: ISceneBundle = {name: scene.name, asset_bundles: []};
            _.map(scene.dependencies, dependencyName => {
                let bundleByName = this.getBundleByName(dependencyName);
                if (bundleByName) sceneBundle.asset_bundles.push(bundleByName);
            });

            // After we have added our dependencies add ourself
            sceneBundle.asset_bundles.push(scene);

            orderedBundles.scene_bundles.push(sceneBundle);
        });

        return orderedBundles;
    }

    /**
     * Convert IStoryBundle to a form that is consumed by the unity app:
     *  {
     *      scene_bundles: [{
     *          name: String
     *          asset_bundles: [String] // url
     *      }],
     *      current_scene_bundle: String
     *  }
     *  JsonStoryBundle.scene_bundles.asset_bundles is an array containing
     *      - the last element of the array is the url of the scene's asset bundles
     *      - each element, except the last, are urls of asset bundles that the scene's asset bundle depends on
     * See also: getStoryBundle
     * @param storyBundle the story bundle to convert
     * @returns JsonStoryBundle
     */
    public static toJsonStoryBundle(storyBundle: IStoryBundle): JsonStoryBundle {
        return {
            scene_bundles: storyBundle.scene_bundles.map(scene => ({
                name: scene.name,
                asset_bundles: scene.asset_bundles.map(bundle => bundle.url)
            })),
            settings_bundle: storyBundle.settings_bundle ? storyBundle.settings_bundle.url : null,
            current_scene_bundle: storyBundle.current_scene_bundle
        };
    }
}
